import React, { useEffect, useState } from 'react'
import styles from './KeyPickupDropAccessModal.module.css'
import pineappleLogo from '../../images/core/pineapple-logo-black-text.svg';
import { useForm } from 'react-hook-form';
import OTPInput from '../common/input/OTPInput/OTPInput';
import { useDispatch } from 'react-redux';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { clearKeyPickupDropEventsState, getKeyPickupDropEventsByAccessCode } from '../../redux/keyPickupDropEvents/actions/keyPickupDropEvents.action';
import { useSelector } from 'react-redux';

interface EventAccessFormData {
  accessCode: string;
  otp: string[];
}

const KeyPickupDropAccessModal = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch: AppDispatch = useDispatch();
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [showNoEvent, setShowNoEvent] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { data: locationData } = useSelector((state: IRootState) => state.locationReducer.locationPublicDetails);
  const { data: keyPickupDropEventData, loading: keyPickupDropEventLoading, error: keyPickupDropEventError } = useSelector((state: IRootState) => state.keyPickupDropEventReducer.KeyPickupDropEvents);
  const {
    handleSubmit,
  } = useForm<EventAccessFormData>({
    defaultValues: {
      otp: Array(6).fill('')
    },
    mode: 'onChange'
  });


  useEffect(() => {
    try {
      if (hasSubmitted && !keyPickupDropEventLoading) {
        const eventId = keyPickupDropEventData?.id;
        if (eventId) {
          navigate(`/key-pickup-drop/${eventId}`);
        } else if (!eventId && !keyPickupDropEventError) {
          setIsLoading(false);
          setShowNoEvent(true);
        } else if (hasSubmitted && !keyPickupDropEventLoading && keyPickupDropEventError) {
          toast.error(keyPickupDropEventError || 'Failed to fetch property event. Please try again.');
          setIsLoading(false);
        }

        setHasSubmitted(false);
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }


  }, [
    hasSubmitted,
    keyPickupDropEventLoading,
    keyPickupDropEventData,
    keyPickupDropEventError,
    navigate,
  ]);


  useEffect(() => {
    localStorage.removeItem('keyPickupDropEventAccess');
    return () => {
      dispatch(clearKeyPickupDropEventsState());
    };
  }, []);

  const onSubmit = async (data: EventAccessFormData) => {
    if (!id) {
      navigate(-1);
      return;
    }
    setIsLoading(true);
    setShowNoEvent(false);
    setHasSubmitted(true);

    try {
      // Convert OTP array to access code string
      const accessCode = otp.join('');
      dispatch(getKeyPickupDropEventsByAccessCode({
        accessCode,
        locationId: id
      }));
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit access code. Please try again.');
      setIsLoading(false);
    }
  };
  const isFormValid = otp.every(digit => digit !== '');

  if (showNoEvent) {
    return (
      <div className={styles.modalContainer}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.logoContainer}>
              <img
                src={pineappleLogo}
                alt="Pineapple Logo"
                className={styles.logo}
              />
            </div>
          </div>

          {/* No Appointment Content */}
          <div className={styles.noAppointmentContent}>
            <div className={styles.noAppointmentIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#8c8d90" />
              </svg>
            </div>
            <h2 className={styles.noAppointmentTitle}>No Events Found</h2>
            <p className={styles.noAppointmentText}>
              There are currently no event scheduled for this location.
              Please contact the office for more information.
            </p>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.submitButton}
                onClick={() => {
                  setShowNoEvent(false);
                  setOtp(Array(6).fill(''));
                  setHasSubmitted(false);
                  dispatch(clearKeyPickupDropEventsState());
                }}
              >
                Try Again
              </button>

              <button
                type="button"
                className={styles.backButton}
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>

            {/* Contact Office Link */}
            <div className={styles.contactSection}>
              <p className={styles.contactText}>
                Need help?
                <a
                  className={styles.contactButton}
                  target='_blank'
                  rel='noopener noreferrer'
                  href={`tel:+${locationData?.data?.leasingAgentNumber || `18887511211p800`}`}
                >
                  Contact Office
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.modalContainer}>
      <div className={styles.modalContent}>

        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.logoContainer}>
            <img
              src={pineappleLogo}
              alt="Pineapple Logo"
              className={styles.logo}
            />
          </div>

        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          {/* OTP Input */}
          <div className={styles.inputGroup}>
            <h2 className={styles.modalTitle}>Key-Pickup & Drop Access</h2>
            <div className={styles.otpContainer}>
              <OTPInput otp={otp} setOtp={setOtp} responsive={true} />
            </div>
            <p className={styles.otpHelperText}>
              Enter the 6-digit access code
            </p>
          </div>

          {/* Submit Button */}
          <div className={styles.buttonGroup}>
            <button
              type="submit"
              className={`${styles.submitButton} ${!isFormValid || isLoading ? styles.submitButtonDisabled : ''}`}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                'Submit'
              )}
            </button>

            <button
              type="button"
              className={`${styles.backButton} ${isLoading ? styles.backButtonDisabled : ''}`}
              disabled={isLoading}
              onClick={() => navigate(-1)}
            >Back</button>
          </div>

          {/* Contact Office Link */}
          <div className={styles.contactSection}>
            <p className={styles.contactText}>
              Need help?
              <a
                className={styles.contactButton}
                target='_blank'
                rel='noopener noreferrer'
                href={`tel:+${locationData?.data?.leasingAgentNumber || `18887511211p800`}`}
              >
                Contact Office
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default KeyPickupDropAccessModal
